import { type SessionV2, types as dqCoreTypes } from "@decentriq/core";
import { loadAsync } from "jszip";
import { useCallback, useMemo } from "react";
import { useApiCore } from "contexts";
import { delay, logWarning, parseMediaDataRoomError } from "utils";
import { type MediaDataRoomJobResultTransform } from "../models";
import {
  type MediaDataRoomComputeRequestKey,
  type MediaDataRoomJobInput,
  type V3MediaComputeRequestValueOfKey,
} from "./models";

export interface MediaDataRoomLazyJobHookPayload<
  T,
  K extends MediaDataRoomComputeRequestKey,
> {
  /** @param input `MediaDataRoomJobInput` includes essential info for sending request, like request key, dataRoomId, driverAttestationHash, etc, please check `MediaDataRoomJobInput` for more details */
  input: MediaDataRoomJobInput<K>;
  /** @param requestCreator function which needs to return body of the request for the given key; if not provided, it will need to be provided in the payload of the runner otherwise an error will be thrown */
  requestCreator?: () => V3MediaComputeRequestValueOfKey<K>;
  /** @param transform function to transform the job result, it provides the zip file in `JSZip` format */
  transform: MediaDataRoomJobResultTransform<T>;
  /** @param session optional `SessionV2`, if not provided, it will be fetched */
  session?: SessionV2;
}

export type MediaDataRoomLazyJobRunner<
  T,
  K extends MediaDataRoomComputeRequestKey,
> = (payload?: {
  /**
   *This function is used to create the request body for the given key, it has priority over the `requestCreator` provided in the hook options
   *
   * @returns body of the request for the given key
   */
  requestCreator?: () => V3MediaComputeRequestValueOfKey<K>;
  /**
   *By providing options you can override the session and scope id used during the request execution
   * @param session `SessionV2` object to be used for the request
   * @param scopeId data scope id to be used for the request
   */
  options?: { session?: SessionV2 };
  updateInput?: (input: MediaDataRoomJobInput<K>) => MediaDataRoomJobInput<K>;
}) => Promise<T>;
export type MediaDataRoomLazyJobHookResult<
  T,
  K extends MediaDataRoomComputeRequestKey,
> = [MediaDataRoomLazyJobRunner<T, K>];

/**
 *This hook is used to create request, fetch the results of the job for using job ID and compute node name from request response on demand.
 *
 * @param input `MediaDataRoomJobInput` includes essential info for sending request, like request key, dataRoomId, etc, please check `MediaDataRoomJobInput` for more details
 * @param requestCreator function which provides `dataRoomIdHex`, `scopeIdHex` and needs to return body of the request for the given key
 * @param session optional `SessionV2`, if not provided, it will be fetched
 * @param transform function to transform the job result, it provides the zip file in `JSZip` format
 * @returns array with the runner function, which returns `Promise` with the transformed job result. If the runner is used on the `AbMediaRequest` key which does not return job ID and compute node name, an error will be thrown
 */
const useMediaDataRoomLazyJob = <T, K extends MediaDataRoomComputeRequestKey>({
  input: payloadInput,
  requestCreator,
  transform,
  ...restPayload
}: MediaDataRoomLazyJobHookPayload<T, K>): MediaDataRoomLazyJobHookResult<
  T,
  K
> => {
  const { key, dataRoomId } = payloadInput;
  const { getSession, client } = useApiCore();
  const handleGettingSession = useCallback(
    async (session?: SessionV2) => session ?? (await getSession()),
    [getSession]
  );
  const downloadJobResult = useCallback(
    async (
      jobId: string,
      resultHash: string,
      session: SessionV2
    ): Promise<Uint8Array> => {
      const [manifestHashHex, encryptionKey] =
        await session.sendRetrieveResultEncryptionKeyRequest(jobId, resultHash);
      const stream = await client.downloadJobResult(
        jobId,
        manifestHashHex,
        resultHash,
        encryptionKey
      );
      return await client.readStream(stream);
    },
    [client]
  );
  const getOrCreateJobId = useCallback<
    (
      dataRoomId: string,
      key: K,
      request: V3MediaComputeRequestValueOfKey<K>,
      skipCheckForExistingJob?: boolean
    ) => Promise<string>
  >(
    async (dataRoomId, key, request, skipCheckForExistingJob = false) => {
      if (!skipCheckForExistingJob) {
        try {
          const action = await client.getDataRoomActionInfo(dataRoomId, {
            [key]: request,
            kind: "media",
          });
          if (
            action.jobs.length &&
            (await client.getJobFreshness(action.jobs[0]))
          ) {
            return action.jobs[0];
          }
        } catch (error) {
          logWarning(
            `Unable to get fresh job or job was not created yet for ${key.toString()}: `,
            error
          );
        }
      }
      return (
        await client.createJob(dataRoomId, {
          [key]: request,
          kind: "media",
        })
      ).id;
    },
    [client]
  );
  const run = useCallback<MediaDataRoomLazyJobRunner<T, K>>(
    async (payload) => {
      if (!payload?.requestCreator && !requestCreator) {
        throw new Error(
          `Failed to create request for "${key.toString()}". requestCreator is not provided neither in payload nor in hook options`
        );
      }
      const input = payload?.updateInput?.(payloadInput) ?? payloadInput;
      const effectiveRequestCreator =
        payload?.requestCreator ?? requestCreator!;
      try {
        const jobId = await getOrCreateJobId(
          dataRoomId,
          input.key,
          effectiveRequestCreator(),
          input.skipCheckForExistingJob
        );
        let jobStatus: dqCoreTypes.JobStatusView;
        while (true) {
          jobStatus = await client.getJobStatus(jobId);
          if (jobStatus.completed_at !== null) {
            break;
          }
          await delay(input.timeout);
        }
        const session =
          payload?.options?.session ??
          restPayload.session ??
          (await handleGettingSession());
        const jobTasks = await client.getJobTasks(jobId);
        if (jobStatus.status === dqCoreTypes.JobStatus.FAILED) {
          const errorResults = [];
          for (const task of Object.values(jobTasks.tasks)) {
            for (const [selector, resultHash] of Object.entries(task.results)) {
              if (
                selector.startsWith("error") &&
                task.completed.includes(selector)
              ) {
                const error = await downloadJobResult(
                  jobId,
                  resultHash,
                  session
                );
                errorResults.push([task.name, new TextDecoder().decode(error)]);
              }
            }
          }
          throw new Error(
            `Job failed with errors: ${errorResults.map(([name, error]) => `${name}: ${error}`).join("\n")}`
          );
        }
        const jobTaskResultHash =
          jobTasks.tasks[jobTasks.target_task].results["success"];
        if (jobTaskResultHash === null) {
          throw new Error("Did not find job task result");
        }
        const resultStream = await downloadJobResult(
          jobId,
          jobTaskResultHash,
          session
        );
        const zip = await loadAsync(resultStream);
        return await transform(zip);
      } catch (error) {
        throw parseMediaDataRoomError(error);
      }
    },
    // Its safe to rely only on `input.queryChangingKey` being changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      key,
      restPayload.session,
      transform,
      handleGettingSession,
      requestCreator,
      payloadInput.queryChangingKey,
    ]
  );
  return useMemo(() => [run], [run]);
};

export default useMediaDataRoomLazyJob;
